import { useMemo } from 'react';
import KeepLink from '../components/KeepLink';
import { useGraphIndex } from '../hooks/useGraphIndex';
import { useLang } from '../hooks/useLang';
import type { AdhigaramEntry, PaalSlug, ThemeEntry } from '../data/types';
import './Adhigarams.css';

const PAAL_ORDER: PaalSlug[] = ['aram', 'porul', 'inbam'];

interface Group {
    paal: PaalSlug;
    paal_title_en: string;
    paal_title_ta: string;
    iyal_en: string;
    iyal_ta: string;
    adhigarams: (AdhigaramEntry & { topThemes: ThemeEntry[] })[];
}

export default function Adhigarams() {
    const graph = useGraphIndex();
    const [lang] = useLang();

    const groups = useMemo<Group[] | null>(() => {
        if (!graph.data) return null;
        const paalBySlug = new Map(graph.data.paals.map((p) => [p.slug, p]));
        const themeBySlug = new Map(graph.data.themes.map((t) => [t.slug, t]));
        const kuralByN = new Map(graph.data.kurals.map((k) => [k.n, k]));

        // Preserve traversal order: paal (aram, porul, inbam) × iyal (order of first
        // appearance in adhigaram_number).
        const out: Group[] = [];
        let current: Group | null = null;
        const seenKey = new Set<string>();

        for (const paalSlug of PAAL_ORDER) {
            const paal = paalBySlug.get(paalSlug);
            if (!paal) continue;
            const adhs = paal.adhigaram_ns
                .map((n) => graph.data!.adhigarams.find((a) => a.n === n))
                .filter((a): a is AdhigaramEntry => a !== undefined);
            for (const a of adhs) {
                const key = `${paalSlug}::${a.iyal_en}`;
                // Top-3 themes: multiset mode across member kural tags,
                // ties → alphabetical.
                const counts = new Map<string, number>();
                for (const kn of a.kural_ns) {
                    const k = kuralByN.get(kn);
                    if (!k) continue;
                    for (const tag of k.tags) {
                        counts.set(tag, (counts.get(tag) ?? 0) + 1);
                    }
                }
                const top3: ThemeEntry[] = [...counts.entries()]
                    .sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))
                    .slice(0, 3)
                    .map(([slug]) => themeBySlug.get(slug))
                    .filter((t): t is ThemeEntry => t !== undefined);

                if (!seenKey.has(key)) {
                    seenKey.add(key);
                    current = {
                        paal: paalSlug,
                        paal_title_en: paal.title_en,
                        paal_title_ta: paal.title_ta,
                        iyal_en: a.iyal_en,
                        iyal_ta: a.iyal,
                        adhigarams: [],
                    };
                    out.push(current);
                }
                current!.adhigarams.push({ ...a, topThemes: top3 });
            }
        }
        return out;
    }, [graph.data]);

    if (graph.error) {
        return (
            <div className="focus-error" role="alert">
                <p>Sorry — we couldn't load the corpus index. Refresh to try again.</p>
            </div>
        );
    }
    if (graph.loading || !groups) {
        return (
            <div className="focus-loading" aria-live="polite">
                <span className="focus-loading__dot" aria-hidden="true" />
                <span>Loading adhigarams…</span>
            </div>
        );
    }

    return (
        <div className="adh-view">
            <header className="adh-view__head">
                <h1 className="adh-view__title">Adhigarams</h1>
                <p className="adh-view__sub">
                    All 133 chapters, grouped by their iyal (section) within each paal
                    (book). Clicking opens the first kural of that chapter.
                </p>
            </header>

            {groups.map((g) => (
                <section
                    key={`${g.paal}-${g.iyal_en}`}
                    className="adh-view__group"
                    aria-labelledby={`iyal-${g.paal}-${g.iyal_en}`}
                >
                    <header
                        className="adh-view__iyal-head"
                        id={`iyal-${g.paal}-${g.iyal_en}`}
                    >
                        <span className="adh-view__paal">
                            {lang === 'ta' ? (
                                <span lang="ta">{g.paal_title_ta}</span>
                            ) : (
                                g.paal_title_en
                            )}
                        </span>
                        <span className="adh-view__iyal">
                            {lang === 'ta' ? (
                                <>
                                    <span lang="ta">{g.iyal_ta}</span>
                                    <span className="adh-view__iyal-sub">{g.iyal_en}</span>
                                </>
                            ) : (
                                <>
                                    <span>{g.iyal_en}</span>
                                    <span lang="ta" className="adh-view__iyal-sub">
                                        {g.iyal_ta}
                                    </span>
                                </>
                            )}
                        </span>
                    </header>

                    <ul className="adh-view__list">
                        {g.adhigarams.map((a) => (
                            <li key={a.n}>
                                <KeepLink
                                    to={`/kural/${a.kural_ns[0]}`}
                                    className="adh-row"
                                    aria-label={`Open Adhigaram ${a.n}: ${a.title_en} (${a.title_ta})`}
                                >
                                    <span className="adh-row__n">{a.n}</span>
                                    <span className="adh-row__title">
                                        {lang === 'ta' ? (
                                            <>
                                                <span lang="ta" className="adh-row__title-lead">
                                                    {a.title_ta}
                                                </span>
                                                <span className="adh-row__title-sub">
                                                    {a.title_en}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="adh-row__title-lead">
                                                    {a.title_en}
                                                </span>
                                                <span lang="ta" className="adh-row__title-sub">
                                                    {a.title_ta}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                    <span className="adh-row__themes">
                                        {a.topThemes.map((t) => (
                                            <span key={t.slug} className="adh-row__theme">
                                                {lang === 'ta' ? (
                                                    <span lang="ta">{t.title_ta}</span>
                                                ) : (
                                                    t.title_en
                                                )}
                                            </span>
                                        ))}
                                    </span>
                                </KeepLink>
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    );
}
